import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Cookie, X, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export function CookieBanner() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Toujours activé
    analytics: false,
    preferences: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Délai pour une meilleure UX (ne pas afficher immédiatement)
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
    // Réappliquer le consentement enregistré à gtag au chargement
    try {
      const saved = JSON.parse(consent);
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: saved.analytics ? 'granted' : 'denied',
        });
      }
    } catch (e) {
      // JSON invalide - on ignore
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      essential: true,
      analytics: true,
      preferences: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consent));
    setIsVisible(false);
    // Activer Google Analytics si accepté
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
      });
    }
  };

  const handleRejectAll = () => {
    const consent = {
      essential: true,
      analytics: false,
      preferences: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consent));
    setIsVisible(false);
    // Désactiver Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
      });
    }
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      essential: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consent));
    setIsVisible(false);
    // Mettre à jour Google Analytics selon les préférences
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: preferences.analytics ? 'granted' : 'denied',
      });
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t border-slate-200 shadow-lg animate-in slide-in-from-bottom duration-300">
      <div className="max-w-6xl mx-auto">
        {!showSettings ? (
          // Bannière principale
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Cookie className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-700 leading-relaxed">
                  {t('cookies.banner.message', 'Nous utilisons des cookies pour améliorer votre expérience sur Drone Maps. Les cookies essentiels sont nécessaires au fonctionnement du site. Vous pouvez accepter ou refuser les cookies optionnels.')}{' '}
                  <Link to="/cookies" className="text-sky-600 hover:underline">{t('cookies.banner.learnMore', 'En savoir plus')}</Link>
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                {t('cookies.banner.configure', 'Paramétrer')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectAll}
              >
                {t('cookies.banner.reject', 'Refuser')}
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="bg-sky-500 hover:bg-sky-600 text-white"
              >
                {t('cookies.banner.acceptAll', 'Tout accepter')}
              </Button>
            </div>
          </div>
        ) : (
          // Panneau de paramétrage
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">{t('cookies.settings.title', 'Paramètres des cookies')}</h3>
              <button 
                onClick={() => setShowSettings(false)}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="space-y-3">
              {/* Cookies essentiels */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">{t('cookies.settings.essential.label', 'Cookies essentiels')}</p>
                  <p className="text-sm text-slate-500">{t('cookies.settings.essential.desc', 'Nécessaires au fonctionnement du site')}</p>
                </div>
                <div className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm rounded-full">
                  {t('cookies.settings.essential.alwaysOn', 'Toujours actifs')}
                </div>
              </div>
              
              {/* Cookies analytiques */}
              <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                <div>
                  <p className="font-medium text-slate-900">{t('cookies.settings.analytics.label', 'Cookies analytiques')}</p>
                  <p className="text-sm text-slate-500">{t('cookies.settings.analytics.desc', 'Nous aident à améliorer le site (Google Analytics)')}</p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                  className="w-5 h-5 accent-sky-500"
                />
              </label>
              
              {/* Cookies de préférences */}
              <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                <div>
                  <p className="font-medium text-slate-900">{t('cookies.settings.preferences.label', 'Cookies de préférences')}</p>
                  <p className="text-sm text-slate-500">{t('cookies.settings.preferences.desc', 'Mémorisent vos préférences (langue, carte)')}</p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.preferences}
                  onChange={(e) => setPreferences({ ...preferences, preferences: e.target.checked })}
                  className="w-5 h-5 accent-sky-500"
                />
              </label>
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectAll}
              >
                {t('cookies.settings.rejectAll', 'Tout refuser')}
              </Button>
              <Button
                size="sm"
                onClick={handleSavePreferences}
                className="bg-sky-500 hover:bg-sky-600 text-white"
              >
                {t('cookies.settings.save', 'Enregistrer mes choix')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
